import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
  useCallback,
} from "react";
import { useTheme } from "../../context/ThemeContext";
import { useIframeThemeSync } from "../../hooks/useIframeThemeSync";
import SuccessIcon from "../../images/success_icon.svg";
import ErrorIcon from "../../images/error_icon.svg";
import { toast } from "react-toastify";
import CustomToastContent from "../../components/CustomToastContent";
import { useLocation } from "react-router-dom";
import OptionChainTable from "../../components/OptionChainTable";
import WebsocketWrapperContext from "../../context/WebsocketWrapperContext";
import { getSymbols } from "../../utils/api";
import { getOptionData } from "../OptionChainScreen/ApiCalls/FetchExpirySymbols";
import { ChevronDown } from "lucide-react";
import ScalpIcon from "../../images/flash.svg";
import { ConfirmationModal } from "../../components/watchlist/components/ConfirmationModal";
import { getCookie } from "../../utils/cookieUtils";
import WatchlistContainer from "../../components/watchlist/WatchlistContainer";

// Configure component for Charts (matching OptionChain screen)
const ChartsConfigure = React.memo(
  ({ configureSettingsMap, setConfigureSettingsMap }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [localSettings, setLocalSettings] = useState(configureSettingsMap);
    const originalSettingsRef = useRef(null);
    const ref = useRef(null);
    const { theme } = useTheme();

    const DEFAULT_SETTINGS = {
      showCalls: true,
      showPuts: true,
      showDelta: false,
      showTheta: false,
      showGamma: false,
      showVega: false,
      showRho: false,
      showOIChng: false,
      showOIBuilddup: false,
      noOfStrikes: 30,
    };

    function deepEqual(a, b) {
      return JSON.stringify(a) === JSON.stringify(b);
    }

    //persisting no. of strikes:
    useEffect(() => {
      if (
        configureSettingsMap.maxStrikes > 0 &&
        configureSettingsMap.noOfStrikes > configureSettingsMap.maxStrikes
      ) {
        setConfigureSettingsMap((prev) => ({
          ...prev,
          noOfStrikes: Math.min(prev.noOfStrikes, prev.maxStrikes),
        }));
      }
    }, [configureSettingsMap.maxStrikes]);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (ref.current && !ref.current.contains(e.target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
      if (isOpen) {
        setLocalSettings(JSON.parse(JSON.stringify(configureSettingsMap)));
        originalSettingsRef.current = JSON.parse(
          JSON.stringify(configureSettingsMap)
        );
      }
    }, [isOpen]);

    const toggleSetting = (key) => {
      setLocalSettings((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const updateNoOfStrikes = (value) => {
      setLocalSettings((prev) => ({ ...prev, noOfStrikes: Number(value) }));
    };

    const handleReset = () => {
      setLocalSettings({ ...DEFAULT_SETTINGS });
    };

    const handleApply = () => {
      setConfigureSettingsMap({ ...localSettings });
      originalSettingsRef.current = JSON.parse(JSON.stringify(localSettings));
      setIsOpen(false);
    };

    const hasChanged =
      originalSettingsRef.current &&
      !deepEqual(localSettings, originalSettingsRef.current);

    return (
      <div className="relative" ref={ref}>
        <button
          className="text-[10px] px-6 py-2 bg-swipe-button text-p400 font-medium rounded flex items-center justify-center gap-2"
          onClick={() => setIsOpen(!isOpen)}>
          <p>Configure</p>
        </button>
        {isOpen && (
          <div className="absolute right-0 z-[9999] w-52 rounded-md shadow-lg bg-p400 border border-n200">
            <div className="px-4 py-3 border-b border-n200 flex justify-between items-center">
              <span className="text-[12px] text-p300 font-medium">
                Settings
              </span>
              <button
                className="text-[12px] font-semibold text-rose-600"
                onClick={handleReset}>
                Reset
              </button>
            </div>

            <div className="px-4 py-3 pt-1 border-b border-n200">
              <span className="text-[12px] text-p300 font-semibold">
                Display
              </span>
              <div className="mt-2 space-y-2">
                <label className="flex items-center gap-2 text-[12px] font-medium text-n500">
                  <input
                    type="checkbox"
                    className="accent-black"
                    checked={localSettings.showCalls}
                    onChange={() => toggleSetting("showCalls")}
                  />{" "}
                  Call
                </label>
                <label className="flex items-center gap-2 text-[12px] font-medium text-n500">
                  <input
                    type="checkbox"
                    className="accent-black"
                    checked={localSettings.showPuts}
                    onChange={() => toggleSetting("showPuts")}
                  />{" "}
                  Put
                </label>
              </div>
            </div>

            <div className="px-4 py-3 pt-1 border-b border-n200">
              <span className="text-[12px] text-p300 font-semibold">
                Show/Hide Column
              </span>
              <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                <label className="flex items-center gap-2 text-[12px] font-medium text-n500">
                  <input
                    type="checkbox"
                    className="accent-black mt-0.5"
                    checked={localSettings.showDelta}
                    onChange={() => toggleSetting("showDelta")}
                  />{" "}
                  Delta
                </label>
                <label className="flex items-center gap-2 text-[12px] font-medium text-n500">
                  <input
                    type="checkbox"
                    className="accent-black"
                    checked={localSettings.showTheta}
                    onChange={() => toggleSetting("showTheta")}
                  />{" "}
                  Theta
                </label>
                <label className="flex items-center gap-2 text-[12px] font-medium text-n500">
                  <input
                    type="checkbox"
                    className="accent-black"
                    checked={localSettings.showGamma}
                    onChange={() => toggleSetting("showGamma")}
                  />{" "}
                  Gamma
                </label>
                <label className="flex items-center gap-2 text-[12px] font-medium text-n500">
                  <input
                    type="checkbox"
                    className="accent-black"
                    checked={localSettings.showVega}
                    onChange={() => toggleSetting("showVega")}
                  />{" "}
                  Vega
                </label>
                <label className="flex items-center gap-2 text-[12px] font-medium text-n500">
                  <input
                    type="checkbox"
                    className="accent-black"
                    checked={localSettings.showRho}
                    onChange={() => toggleSetting("showRho")}
                  />{" "}
                  Rho
                </label>
              </div>
            </div>

            <div className="px-4 py-3">
              <div className="flex items-center justify-between mt-2">
                <div className="text-[12px] text-p300 font-semibold">
                  No. Of Strikes
                </div>
                <div className="text-center text-[10px] border-n200 text-p300 font-medium border rounded-sm px-1 py-0.5">
                  {localSettings.noOfStrikes}
                </div>
              </div>
              <input
                type="range"
                min="2"
                max={configureSettingsMap.maxStrikes}
                value={localSettings.noOfStrikes}
                onChange={(e) => updateNoOfStrikes(e.target.value)}
                className="w-full accent-black"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] text-p300 font-medium">2</span>
                <span className="text-[10px] text-p300 font-medium">
                  {configureSettingsMap.maxStrikes}
                </span>
              </div>
            </div>

            <div className="px-4 py-3 border-t border-n200">
              <button
                disabled={!hasChanged}
                className={`text-[12px] py-2 font-semibold rounded flex gap-2 w-full justify-center items-center ${hasChanged ? "bg-swipe-button text-p400" : "bg-n500 text-white"}`}
                onClick={handleApply}>
                <img
                  src="/execute.svg"
                  alt="Execute icon"
                  className="w-4 h-4 mt-0.5"
                  style={{
                    filter:
                      theme === "dark" ? "brightness(0) invert(1)" : "none",
                  }}
                />
                <p>Apply</p>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
);

// ExpiryDropdown component for Charts (memoized to prevent re-renders)
const ChartsExpiryDropdown = React.memo(
  ({ expiry, expiries, onSelect, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef();

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (ref.current && !ref.current.contains(e.target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
      <div className="relative" ref={ref}>
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="px-2 py-2 text-[10px] font-semibold border border-n300 rounded bg-p400 text-p300 cursor-pointer flex justify-between items-center gap-1 min-w-24">
          <span>{label && label}</span>
          <span>{expiry || "Select Expiry"}</span>
          <ChevronDown size={12} className="text-p300" />
        </div>

        {isOpen && (
          <ul className="absolute z-10 w-full bg-p400 text-p300 border border-n300 rounded shadow-md h-[100px] overflow-auto text-[10px] no-scrollbar">
            {expiries?.map((date, idx) => (
              <li
                key={idx}
                className="py-1 px-2 hover:bg-n200 cursor-pointer"
                onClick={() => {
                  onSelect(date);
                  setIsOpen(false);
                }}>
                {date}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }
);

/**
 * Charts Component - Performance Optimized Architecture
 *
 * Key principles:
 * 1. Iframe loads ONCE with stable src - never reloads on symbol changes
 * 2. Symbol changes communicated via postMessage (no iframe remounts)
 * 3. State (chartsSelectedSymbol) is source of truth
 * 4. URL params only read on initial load, then ignored
 * 5. Session storage provides persistence across navigation
 *
 * Flow:
 * - Initial load: URL params → state OR session storage → state
 * - Symbol change: state updates → postMessage to iframe
 * - Iframe feedback: postMessage from iframe → state updates
 */
function Charts({ visible }) {
  // Charts-specific state (completely isolated from other contexts)
  const [chartsAllSymbols, setChartsAllSymbols] = useState(null);
  const [chartsSelectedSymbol, setChartsSelectedSymbol] = useState(null);
  const [chartsAllOptionsData, setChartsAllOptionsData] = useState({});
  const [chartsCurrentOptionsData, setChartsCurrentOptionsData] = useState({});
  const [chartsSelectedExpiry, setChartsSelectedExpiry] = useState("");
  const [chartsSelectedData, setChartsSelectedData] = useState([]); // Dummy state for OptionChainTable
  const [chartsActiveTab, setChartsActiveTab] = useState("Nifty"); // Separate active tab for Charts
  const [chartsScalpMode, setChartsScalpMode] = useState(() => {
    const saved = localStorage.getItem("chartsScalpMode");
    return saved ? JSON.parse(saved) : false;
  });
  const [chartsScalpLotsInput, setChartsScalpLotsInput] = useState(() => {
    const saved = localStorage.getItem("chartsScalpLotsInput");
    return saved || "1";
  });
  const [showScalpConfirmation, setShowScalpConfirmation] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState("optionChain"); // "optionChain" or "watchlist"

  // State to track current ATM information
  const [currentATM, setCurrentATM] = useState({
    strikeIndex: 0,
    strikePrice: 0,
    effectiveLtp: 0,
    method: 'unknown'
  });

  // Charts-specific configure settings (isolated from BuilderContext)
  const [chartsConfigureSettingsMap, setChartsConfigureSettingsMap] = useState(
    () => {
      const saved = localStorage.getItem("chartsConfigureSettingsMap");
      return saved
        ? JSON.parse(saved)
        : {
          showCalls: true,
          showPuts: true,
          showDelta: false,
          showTheta: false,
          showGamma: false,
          showVega: false,
          showRho: false,
          showOIChng: false,
          showOIBuilddup: false,
          noOfStrikes: 30,
          maxStrikes: 30,
        };
    }
  );

  // Dummy function for OptionChainTable (not used in Charts)
  const chartsHandleLotxChange = () => {
    // This function is not used in Charts screen
  };

  // Save charts configure settings to localStorage
  useEffect(() => {
    localStorage.setItem(
      "chartsConfigureSettingsMap",
      JSON.stringify(chartsConfigureSettingsMap)
    );
  }, [chartsConfigureSettingsMap]);

  // Save scalp mode state to localStorage
  useEffect(() => {
    localStorage.setItem("chartsScalpMode", JSON.stringify(chartsScalpMode));
  }, [chartsScalpMode]);

  // Save scalp lots input to localStorage
  useEffect(() => {
    localStorage.setItem("chartsScalpLotsInput", chartsScalpLotsInput);
  }, [chartsScalpLotsInput]);

  // Update maxStrikes when option data changes
  useEffect(() => {
    if (chartsCurrentOptionsData[chartsSelectedExpiry]?.options) {
      const totalStrikes =
        chartsCurrentOptionsData[chartsSelectedExpiry].options.length;
      if (chartsConfigureSettingsMap.maxStrikes !== totalStrikes) {
        setChartsConfigureSettingsMap((prev) => ({
          ...prev,
          maxStrikes: totalStrikes,
        }));
      }
    }
  }, [
    chartsCurrentOptionsData[chartsSelectedExpiry],
    chartsConfigureSettingsMap.maxStrikes,
  ]);

  const { theme } = useTheme();
  const {
    checkWebsocketConnection,
    subscribeToSymbols,
    unsubscribeToSymbols,
    symbolList,
    symbolData,
  } = useContext(WebsocketWrapperContext);

  const ltpValue = symbolData[chartsSelectedSymbol?.code]?.ltp || 0;

  // Function to calculate ATM in real-time based on current LTP
  const calculateATM = useCallback((optionsData, expiry, currentLtp) => {
    if (!optionsData || !optionsData[expiry] || !optionsData[expiry].options) {
      return {
        strikeIndex: 0,
        strikePrice: 0,
        effectiveLtp: 0,
        method: 'no_data'
      };
    }

    const formattedData = optionsData[expiry].options;
    if (formattedData.length === 0) {
      return {
        strikeIndex: 0,
        strikePrice: 0,
        effectiveLtp: 0,
        method: 'empty_data'
      };
    }

    const underlying_ltp = currentLtp || 0;
    let effectiveLtp = underlying_ltp;
    let closestIdx = 0;
    let method = 'unknown';

    if (effectiveLtp === 0 || effectiveLtp === null) {
      // Try to use spot price from option data first
      if (optionsData[expiry]?.spot) {
        effectiveLtp = parseFloat(optionsData[expiry].spot);
        method = 'spot_price';

        // Find closest strike to spot price
        let minDiff = Number.POSITIVE_INFINITY;
        formattedData.forEach((item, index) => {
          const diff = Math.abs(item.strike - effectiveLtp);
          if (diff < minDiff) {
            minDiff = diff;
            closestIdx = index;
          }
        });
      } else {
        // When no spot price available, use highest OI approach
        let maxCombinedOI = 0;
        let atmStrikeIndex = Math.floor(formattedData.length / 2); // Default to middle
        let hasValidOI = false;

        formattedData.forEach((item, index) => {
          const callOI = parseFloat(item.call?.oi || 0);
          const putOI = parseFloat(item.put?.oi || 0);
          const combinedOI = callOI + putOI;

          if (combinedOI > 0) {
            hasValidOI = true;
            if (combinedOI > maxCombinedOI) {
              maxCombinedOI = combinedOI;
              atmStrikeIndex = index;
            }
          }
        });

        closestIdx = atmStrikeIndex;
        effectiveLtp = formattedData[closestIdx]?.strike || 0;
        method = hasValidOI ? 'highest_oi' : 'middle_strike';
      }
    } else {
      // LTP is available, find closest strike to actual LTP
      method = 'real_ltp';
      let minDiff = Number.POSITIVE_INFINITY;
      formattedData.forEach((item, index) => {
        const diff = Math.abs(item.strike - parseFloat(effectiveLtp));
        if (diff < minDiff) {
          minDiff = diff;
          closestIdx = index;
        }
      });
    }

    const atmInfo = {
      strikeIndex: closestIdx,
      strikePrice: formattedData[closestIdx]?.strike || 0,
      effectiveLtp: effectiveLtp,
      method: method
    };

    // console.log("🎯 Real-time ATM Calculation:");
    // console.log("  - Underlying LTP:", underlying_ltp);
    // console.log("  - Effective LTP:", effectiveLtp);
    // console.log("  - Method:", method);
    // console.log("  - ATM Strike:", atmInfo.strikePrice, "at index:", closestIdx);

    return atmInfo;
  }, []);

  // Debug LTP reception and trigger real-time ATM calculation
  useEffect(() => {
    if (chartsSelectedSymbol?.code && symbolData[chartsSelectedSymbol.code]) {
      const currentLtp = symbolData[chartsSelectedSymbol.code].ltp;
      // if (currentLtp && currentLtp !== "0" && currentLtp !== 0) {
      //   console.log("📊 LTP received for", chartsSelectedSymbol.scrip, ":", currentLtp);
      // }
    }
  }, [symbolData, chartsSelectedSymbol?.code]);

  // Real-time ATM calculation when LTP or options data changes
  useEffect(() => {
    if (chartsCurrentOptionsData[chartsSelectedExpiry] && chartsSelectedExpiry) {
      const newATM = calculateATM(chartsCurrentOptionsData, chartsSelectedExpiry, ltpValue);

      // Only update if ATM has actually changed
      if (newATM.strikeIndex !== currentATM.strikeIndex ||
        newATM.strikePrice !== currentATM.strikePrice ||
        newATM.method !== currentATM.method) {
        console.log("🔄 ATM Updated in Real-time:", newATM);
        setCurrentATM(newATM);
      }
    }
  }, [ltpValue, chartsCurrentOptionsData, chartsSelectedExpiry, calculateATM, currentATM.strikeIndex, currentATM.strikePrice, currentATM.method]);

  // State to track LTP availability and trigger retries
  const [ltpRetryCount, setLtpRetryCount] = useState(0);
  const ltpRetryTimerRef = useRef(null);

  // Monitor LTP availability and retry subscription if needed
  useEffect(() => {
    if (!chartsSelectedSymbol?.code) return;

    // Clear any existing retry timer
    if (ltpRetryTimerRef.current) {
      clearTimeout(ltpRetryTimerRef.current);
      ltpRetryTimerRef.current = null;
    }

    // If LTP is available, reset retry count
    if (ltpValue && ltpValue > 0) {
      if (ltpRetryCount > 0) {
        console.log("✅ LTP received after", ltpRetryCount, "retries for", chartsSelectedSymbol.scrip);
        setLtpRetryCount(0);
      }
      return;
    }

    // LTP not available - retry subscription with exponential backoff (less aggressive)
    const maxRetries = 3; // Reduced from 8 to 3
    const retryDelay = Math.min(2000 * Math.pow(1.5, ltpRetryCount), 8000); // Max 8 seconds, less aggressive

    if (ltpRetryCount < maxRetries) {
      console.log(`⏳ LTP not available for ${chartsSelectedSymbol.scrip}, retry ${ltpRetryCount + 1}/${maxRetries} in ${retryDelay}ms`);

      ltpRetryTimerRef.current = setTimeout(() => {
        console.log(`🔄 Retrying subscription for ${chartsSelectedSymbol.scrip} (attempt ${ltpRetryCount + 1})`);

        // Re-subscribe to the symbol
        subscribeToSymbols(
          [chartsSelectedSymbol.code],
          chartsSelectedSymbol.exchange || "NSE"
        );

        setLtpRetryCount(prev => prev + 1);
      }, retryDelay);
    } else if (ltpRetryCount >= maxRetries) {
      console.warn(`⚠️ Max retries reached for ${chartsSelectedSymbol.scrip}, will use fallback mechanisms`);
    }

    // Cleanup timer on unmount
    return () => {
      if (ltpRetryTimerRef.current) {
        clearTimeout(ltpRetryTimerRef.current);
      }
    };
  }, [ltpValue, chartsSelectedSymbol, ltpRetryCount, subscribeToSymbols]);

  const iframeRef = useRef(null);
  const hasInitialized = useRef(false);
  const hasInitialized2 = useRef(false);
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [currentChartSymbol, setCurrentChartSymbol] = useState(null);
  const [objectData, setObjectData] = useState({});
  const [calls, setCalls] = useState([]);
  const [puts, setPuts] = useState([]);
  const [maxPain, setMaxPain] = useState(0);
  const [activeDate, setActiveDate] = useState("");

  // Initialize Charts symbols independently - OPTIMIZED FOR SPEED
  useEffect(() => {
    // STEP 1: Instant load from cache (before API call)
    const query = new URLSearchParams(location.search);
    const codeFromUrl = query.get("code");
    const storedSymbol = sessionStorage.getItem("chartsSelectedSymbol");

    // If we have session storage, use it immediately to show UI fast
    if (!codeFromUrl && storedSymbol) {
      try {
        const parsedSymbol = JSON.parse(storedSymbol);
        console.log("⚡ Instant load from session storage:", parsedSymbol.scrip);
        setChartsSelectedSymbol(parsedSymbol);
      } catch (e) {
        console.error("Failed to parse stored symbol");
      }
    }

    // STEP 2: Fetch symbols in background to validate/update
    const fetchSymbols = async () => {
      const symbols = await getSymbols();
      if (!symbols) return;

      setChartsAllSymbols(symbols);

      // Validate URL code if present
      if (codeFromUrl) {
        const symbolFromUrl = symbols.find((symbol) => symbol.code === codeFromUrl);
        if (symbolFromUrl) {
          console.log("✅ Validated from URL:", symbolFromUrl.scrip, symbolFromUrl.code);
          setChartsSelectedSymbol(symbolFromUrl);
          sessionStorage.setItem("chartsSelectedSymbol", JSON.stringify(symbolFromUrl));
          return;
        }
      }

      // If session storage was used, validate it
      if (storedSymbol) {
        const parsedSymbol = JSON.parse(storedSymbol);
        const validSymbol = symbols.find((s) => s.code === parsedSymbol.code);
        if (validSymbol) {
          console.log("✅ Session storage validated");
          // Already set above for speed, just ensure it's valid
          setChartsSelectedSymbol(validSymbol);
          return;
        }
      }

      // Fallback: default to NIFTY only if no cache
      const niftySymbol = symbols.find((symbol) => symbol.scrip === "NIFTY");
      console.log("✅ Loading default NIFTY:", niftySymbol?.code);
      setChartsSelectedSymbol(niftySymbol);
      if (niftySymbol) {
        sessionStorage.setItem("chartsSelectedSymbol", JSON.stringify(niftySymbol));
      }
    };

    fetchSymbols();
  }, [location.search, location.pathname]);

  // Fetch option data for Charts symbol
  useEffect(() => {
    if (!chartsSelectedSymbol) return;

    console.log("🔄 Fetching option data for symbol:", chartsSelectedSymbol.scrip, chartsSelectedSymbol.code);

    // Reset retry count for new symbol
    setLtpRetryCount(0);

    // Reset ATM state for new symbol
    setCurrentATM({
      strikeIndex: 0,
      strikePrice: 0,
      effectiveLtp: 0,
      method: 'unknown'
    });

    // Subscribe to the new symbol
    console.log("📡 Subscribing to underlying symbol:", chartsSelectedSymbol.scrip, chartsSelectedSymbol.code, chartsSelectedSymbol.exchange || "NSE");
    subscribeToSymbols(
      [chartsSelectedSymbol.code],
      chartsSelectedSymbol.exchange || "NSE"
    );

    // Set the first expiry as default
    setChartsSelectedExpiry(chartsSelectedSymbol?.option_expiries?.[0]);

    const fetchAllOptions = async () => {
      if (!chartsSelectedSymbol?.option_expiries || !chartsSelectedSymbol.scrip)
        return;

      const expiryDates = chartsSelectedSymbol.option_expiries;
      console.log("📅 Fetching option data for", expiryDates.length, "expiries");

      const requests = expiryDates.map(async (date) => {
        const formattedDate = date.replaceAll(" ", "%20");
        const data = await getOptionData(
          chartsSelectedSymbol.scrip,
          formattedDate
        );
        console.log("✅ Fetched data for expiry:", date, "strikes:", data?.options?.length || 0);
        return { [date]: data };
      });

      const results = await Promise.all(requests);
      const mergedData = Object.assign({}, ...results);

      console.log("✅ All option data fetched and merged for", chartsSelectedSymbol.scrip);

      setChartsAllOptionsData((prev) => ({
        ...prev,
        [chartsSelectedSymbol.scrip]: mergedData,
      }));
    };

    fetchAllOptions();

    // Cleanup function to unsubscribe from previous symbol
    return () => {
      if (chartsSelectedSymbol?.code) {
        console.log("🧹 Cleaning up subscription for", chartsSelectedSymbol.code);
        unsubscribeToSymbols([chartsSelectedSymbol.code]);
      }
    };
  }, [chartsSelectedSymbol?.code, chartsSelectedSymbol?.scrip]); // Added chartsSelectedSymbol?.code dependency

  // Update current options data for Charts
  useEffect(() => {
    if (
      !chartsSelectedSymbol ||
      !chartsAllOptionsData[chartsSelectedSymbol?.scrip]
    ) {
      console.log("⏸️ Waiting for option data for", chartsSelectedSymbol?.scrip);
      return;
    }

    const symbolData = chartsAllOptionsData[chartsSelectedSymbol.scrip];
    if (!symbolData) return;

    console.log("📊 Updating chartsCurrentOptionsData for", chartsSelectedSymbol.scrip, "with", Object.keys(symbolData).length, "expiries");
    setChartsCurrentOptionsData(symbolData);
  }, [chartsSelectedSymbol?.scrip, chartsAllOptionsData]);

  // Memoized callback for expiry selection to prevent unnecessary re-renders
  const handleExpirySelect = useCallback(
    (selected) => {
      setChartsSelectedExpiry(selected);
      // Expiry change logging removed

      // Force re-subscription to option symbols when expiry changes
      if (chartsSelectedSymbol && chartsCurrentOptionsData[selected]) {
        const { options } = getFilteredOptionsData(
          chartsCurrentOptionsData,
          selected
        );
        if (options && options.length > 0) {
          const call = getCodeTradingSymbolMappings(options, "call");
          const put = getCodeTradingSymbolMappings(options, "put");
          const tradingSymbols = call.concat(put);

          // Re-subscribing logging removed
          subscribeToSymbols(tradingSymbols, options[0]?.call?.exchange);
        }
      }
    },
    [chartsSelectedSymbol, chartsCurrentOptionsData, subscribeToSymbols, ltpRetryCount]
  );

  useEffect(() => {
    const listener = (event) => {
      // Message logging removed
    };

    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, []);

  // Prepare calls and puts data for OptionChainTable
  useEffect(() => {
    if (
      chartsCurrentOptionsData[chartsSelectedExpiry] &&
      chartsCurrentOptionsData[chartsSelectedExpiry].options
    ) {
      const { options } = getFilteredOptionsData(
        chartsCurrentOptionsData,
        chartsSelectedExpiry
      );

      if (options && options.length > 0) {
        let callsdata = options.map((option) => option.call);
        let putsdata = options.map((option) => option.put);
        setCalls(callsdata);
        setPuts(putsdata);
      } else {
        setCalls([]);
        setPuts([]);
      }
    }
  }, [
    chartsCurrentOptionsData,
    chartsSelectedExpiry,
    chartsConfigureSettingsMap.noOfStrikes,
    currentATM.strikeIndex, // Re-run when ATM changes
  ]);

  // Calculate max pain
  useEffect(() => {
    const { options } = getFilteredOptionsData(chartsCurrentOptionsData, chartsSelectedExpiry);

    if (options && options.length > 0) {
      const strikeOpenInterest = {};
      let maxTotalOi = 0;
      let maxPainStrike = null;

      options.forEach((option) => {
        const strike = option.call.strike;
        if (!strikeOpenInterest[strike]) {
          strikeOpenInterest[strike] = { callOi: 0, putOi: 0 };
        }
        // Add open interest data if available
        strikeOpenInterest[strike].callOi += parseFloat(option.call.oi || 0);
        strikeOpenInterest[strike].putOi += parseFloat(option.put.oi || 0);
      });

      for (const strike in strikeOpenInterest) {
        const { callOi, putOi } = strikeOpenInterest[strike];
        const totalOi = callOi + putOi;

        if (totalOi > maxTotalOi) {
          maxTotalOi = totalOi;
          maxPainStrike = strike;
        }
      }

      if (maxPainStrike) {
        setMaxPain(maxPainStrike);
      }
    }
  }, [chartsCurrentOptionsData, chartsSelectedExpiry, chartsConfigureSettingsMap.noOfStrikes, iframeRef, currentATM.strikeIndex]);

  // Callback function for OptionChainTable
  const handleCallback2 = (childData, buy_sell) => {
    // This function handles when user clicks on option chain items
    // Option chain callback logging removed
    // You can implement navigation or other actions here
  };

  // Removed passedSymbol - we now use chartsSelectedSymbol state exclusively

  // Update URL when chart symbol changes (without triggering iframe reload)
  const prevSymbolCodeRef = useRef(null);

  useEffect(() => {
    if (chartsSelectedSymbol?.code) {
      // Only update URL if the code has changed and it's not the initial load
      if (prevSymbolCodeRef.current !== null && prevSymbolCodeRef.current !== chartsSelectedSymbol.code) {
        const query = new URLSearchParams(location.search);
        query.set("code", chartsSelectedSymbol.code);
        if (chartsSelectedSymbol.scrip) {
          query.set("scrip", chartsSelectedSymbol.scrip);
        }
        if (chartsSelectedSymbol.exchange) {
          query.set("exchange", chartsSelectedSymbol.exchange);
        }

        // Use window.history.replaceState to avoid triggering re-renders
        const newUrl = `${location.pathname}?${query.toString()}`;
        window.history.replaceState({}, '', newUrl);
      }

      prevSymbolCodeRef.current = chartsSelectedSymbol.code;
    }
  }, [chartsSelectedSymbol?.code, chartsSelectedSymbol?.scrip, chartsSelectedSymbol?.exchange, location.pathname]);

  // Stable iframe src - calculated ONCE and never changes
  const iframeSrc = useMemo(() => {
    const accessToken = getCookie("access_token");
    const webSocketToken = getCookie("ws_token");
    const clientId = getCookie("client_id");
    const theme1 = localStorage.getItem("theme");
    const baseUrl = "https://charts.1lyoptions.com/";

    // Get initial code from URL or session storage
    const query = new URLSearchParams(location.search);
    const urlCode = query.get("code");
    const sessionSymbol = sessionStorage.getItem("chartsSelectedSymbol");
    const initialCode = urlCode || (sessionSymbol ? JSON.parse(sessionSymbol).code : "26000");

    console.log("🎯 Creating stable iframe src with initial code:", initialCode);

    const params = new URLSearchParams({
      client_id: clientId,
      access_token: accessToken,
      ws_token: webSocketToken,
      code: initialCode,
      theme: theme1,
    });

    return `${baseUrl}?${params.toString()}`;
  }, []); // Empty deps - calculated once and never changes!

  // Removed debug logging useEffect

  // If no symbol from URL, try to set a default symbol
  useEffect(() => {
    if (
      !currentChartSymbol &&
      chartsAllSymbols &&
      chartsAllSymbols.length > 0 &&
      !loading
    ) {
      // Try to find NIFTY or use first symbol
      const defaultSymbol =
        chartsAllSymbols.find((s) => s.scrip === "NIFTY") ||
        chartsAllSymbols[0];
      if (defaultSymbol) {
        setCurrentChartSymbol(defaultSymbol);
      }
    }
  }, [currentChartSymbol, chartsAllSymbols, loading]);

  // Use custom hook for theme synchronization (with stable initial code)
  const themeParams = useMemo(() => {
    const query = new URLSearchParams(location.search);
    const urlCode = query.get("code");
    const sessionSymbol = sessionStorage.getItem("chartsSelectedSymbol");
    const initialCode = urlCode || (sessionSymbol ? JSON.parse(sessionSymbol).code : "26000");

    return {
      client_id: localStorage.getItem("client_id"),
      access_token: localStorage.getItem("access_token"),
      ws_token: localStorage.getItem("ws_token"),
      code: initialCode,
    };
  }, []); // Stable - calculated once

  const { updateIframeTheme } = useIframeThemeSync(
    iframeRef,
    "https://charts.1lyoptions.com/",
    themeParams
  );

  useEffect(() => {
    if (hasInitialized.current) {
      hasInitialized.current = true;
    }
  }, []);

  // Navigate iframe when symbol changes (after initial mount)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (!chartsSelectedSymbol || !iframeRef.current) return;

    // Skip on initial mount - iframe loads with correct symbol
    if (isInitialMount.current) {
      isInitialMount.current = false;
      console.log("🏁 Initial mount - skipping postMessage, iframe loads with symbol:", chartsSelectedSymbol.code);
      return;
    }

    // On subsequent changes, navigate iframe via postMessage
    console.log("🚀 Navigating iframe to symbol:", chartsSelectedSymbol.scrip, chartsSelectedSymbol.code);
    iframeRef.current.contentWindow?.postMessage(
      {
        type: "chartNavigation",
        payload: {
          scrip: chartsSelectedSymbol.scrip,
          exchange: chartsSelectedSymbol.exchange,
          code: chartsSelectedSymbol.code
        }
      },
      "*"
    );
  }, [chartsSelectedSymbol?.code]);

  // Handle option codes in URL - when clicking options from option chain table
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const codeFromUrl = query.get("code");

    // Skip if no code in URL or iframe not ready or still loading
    if (!codeFromUrl || !iframeRef.current || loading) return;

    // Skip if no symbols loaded yet
    if (!chartsAllSymbols || chartsAllSymbols.length === 0) return;

    // Check if this code is an underlying symbol or an option code
    const isUnderlyingSymbol = chartsAllSymbols.some(symbol => symbol.code === codeFromUrl);

    // If it's NOT an underlying symbol, it must be an option code
    if (!isUnderlyingSymbol) {
      console.log("📊 Option code detected in URL:", codeFromUrl);

      // Find the option details in calls or puts arrays
      let optionDetails = null;

      // Search in calls
      const callOption = calls.find(call => call.code === codeFromUrl);
      if (callOption) {
        optionDetails = {
          tradingSymbol: callOption.trading_symbol,
          exchange: callOption.exchange,
          code: callOption.code
        };
      } else {
        // Search in puts
        const putOption = puts.find(put => put.code === codeFromUrl);
        if (putOption) {
          optionDetails = {
            tradingSymbol: putOption.trading_symbol,
            exchange: putOption.exchange,
            code: putOption.code
          };
        }
      }

      if (optionDetails) {
        console.log("✅ Found option, sending to iframe via postMessage:", optionDetails.tradingSymbol);

        // Send to iframe via postMessage (instant, no reload)
        iframeRef.current.contentWindow?.postMessage(
          {
            type: "chartNavigation",
            payload: {
              scrip: optionDetails.tradingSymbol,
              exchange: optionDetails.exchange,
              code: optionDetails.code
            }
          },
          "*"
        );
      } else {
        console.warn("⚠️ Option not found in calls/puts arrays, code:", codeFromUrl);
      }
    }
  }, [location.search, chartsAllSymbols, calls, puts, loading]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (typeof event.data === "string") {
        if (event.data.includes("symbol code:")) {
          const extractedSymbol = event.data.split(":").pop()?.trim();
          console.log("📨 Iframe posted symbol code:", extractedSymbol);

          // Guard: If it's the same symbol, don't re-trigger everything
          if (chartsSelectedSymbol?.code === extractedSymbol) {
            console.log("✋ Symbol already selected, ignoring iframe message");
            return;
          }

          // Find the symbol by code and load option chain data
          if (chartsAllSymbols && extractedSymbol) {
            const symbol = chartsAllSymbols.find(symbol => symbol.code === extractedSymbol);
            if (symbol) {
              console.log("📊 Setting symbol from iframe message:", symbol.scrip, symbol.code);

              // Set the selected symbol in charts state (not BuilderContext)
              setChartsSelectedSymbol(symbol);

              // Set the active tab based on symbol type
              if (symbol.scrip === "NIFTY") {
                setChartsActiveTab("Nifty");
              } else if (symbol.scrip === "BANKNIFTY") {
                setChartsActiveTab("BankNifty");
              } else if (symbol.scrip === "FINNIFTY") {
                setChartsActiveTab("FinNifty");
              } else {
                setChartsActiveTab("Others");
              }

              // Store in session storage for persistence
              sessionStorage.setItem(
                "chartsSelectedSymbol",
                JSON.stringify(symbol)
              );
            } else {
              console.warn("⚠️ Symbol not found for code:", extractedSymbol);
            }
          }
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [chartsAllSymbols, chartsSelectedSymbol?.code]);

  const getCodeTradingSymbolMappings = (data, key) => {
    const codeTradingSymbol = [];
    for (const item of data) {
      const { code } = item[key];
      codeTradingSymbol.push(code);
    }
    return codeTradingSymbol;
  };

  useEffect(() => {
    const { options } = getFilteredOptionsData(
      chartsCurrentOptionsData,
      chartsSelectedExpiry
    );

    if (options && options.length > 0) {
      const call = getCodeTradingSymbolMappings(options, "call");
      const put = getCodeTradingSymbolMappings(options, "put");
      const tradingSymbols = call.concat(put);

      console.log("📡 Subscribing to", tradingSymbols.length, "option symbols");

      subscribeToSymbols(tradingSymbols, options[0]?.call?.exchange);

      // Cleanup function to unsubscribe from option symbols when dependencies change
      return () => {
        if (tradingSymbols.length > 0) {
          console.log("📡 Unsubscribing from", tradingSymbols.length, "option symbols");
          unsubscribeToSymbols(tradingSymbols);
        }
      };
    }
  }, [
    chartsCurrentOptionsData,
    chartsSelectedExpiry,
    chartsConfigureSettingsMap.noOfStrikes,
    chartsSelectedSymbol?.scrip,
    chartsSelectedSymbol?.code,
    currentATM.strikeIndex, // Re-run when ATM changes
  ]);

  // Add a test function to manually set a symbol (for debugging)
  const setTestSymbol = () => {
    if (chartsAllSymbols && chartsAllSymbols.length > 0) {
      const testSymbol =
        chartsAllSymbols.find((s) => s.scrip === "NIFTY") ||
        chartsAllSymbols[0];
      setCurrentChartSymbol(testSymbol);
      // Test symbol set logging removed
    }
  };

  // Helper function to filter options based on noOfStrikes setting using current ATM
  const getFilteredOptionsData = (optionsData, expiry) => {
    console.log("🔍 getFilteredOptionsData called with optionsData:", optionsData, "and expiry:", expiry);
    if (!optionsData || !optionsData[expiry] || !optionsData[expiry].options) {
      return { optionData: null, options: null };
    }

    const formattedData = optionsData[expiry].options;

    if (formattedData.length === 0) {
      return {
        optionData: optionsData[expiry],
        options: formattedData,
      };
    }

    // Use current ATM information for filtering
    const userSelectedStrikes = chartsConfigureSettingsMap.noOfStrikes;
    const half = Math.floor(userSelectedStrikes / 2);
    const closestIdx = currentATM.strikeIndex;

    console.log("🎯 Using Current ATM for Filtering:");
    console.log("  - ATM Strike Index:", closestIdx);
    console.log("  - ATM Strike Price:", currentATM.strikePrice);
    console.log("  - ATM Method:", currentATM.method);
    console.log("  - User selected strikes:", userSelectedStrikes);

    // Filter options based on noOfStrikes setting, centered around current ATM
    const selected = formattedData.slice(
      Math.max(0, closestIdx - half),
      Math.min(formattedData.length, closestIdx + half + 1)
    );

    console.log("  - Selected strikes range:", selected.length, "strikes from", selected[0]?.strike, "to", selected[selected.length - 1]?.strike);

    return {
      optionData: {
        ...optionsData[expiry],
        options: selected,
        effectiveLtp: currentATM.effectiveLtp,
      },
      options: selected,
      effectiveLtp: currentATM.effectiveLtp,
    };
  };

  // Retry mechanism for loading option data
  const retryLoadOptionData = () => {
    console.log("🔄 Retrying option data load...");
    if (chartsSelectedSymbol && chartsSelectedExpiry) {
      // Force a re-render by updating the expiry
      setChartsSelectedExpiry(chartsSelectedExpiry);
    }
  };

  // Scalp mode confirmation handlers
  const handleScalpModeConfirm = () => {
    setChartsScalpMode(true);
    setShowScalpConfirmation(false);
  };

  const handleScalpModeCancel = () => {
    setShowScalpConfirmation(false);
  };

  return (
    <div
      className="flex h-[85vh] overflow-hidden mt-12 bg-body"
      style={{ display: visible ? "flex" : "none" }}>
      {/* Charts iframe - 50% width */}
      <div className="w-3/5 h-[85vh] relative overflow-hidden">
        {/* Loading overlay - shows while iframe loads */}
        {(loading || !chartsSelectedSymbol) && (
          <div className="absolute inset-0 flex items-center justify-center bg-p400 z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-p300 mx-auto mb-4"></div>
              <div className="text-p300 font-medium">
                {!chartsSelectedSymbol ? "Initializing..." : "Loading Charts..."}
              </div>
            </div>
          </div>
        )}
        {/* Iframe always mounted - never unmounts */}
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          title="Charts"
          className="w-full h-full border-0"
          onLoad={() => {
            console.log("✅ Iframe loaded");
            setLoading(false);
          }}
        />
      </div>

      {/* Right side - Tab View with Option Chain and Watchlist */}
      <div className="w-2/5 max-h-[100vh] bg-p400 overflow-hidden border-l border-n200 flex flex-col">
        {/* Tab Navigation */}
        <div className="flex border-b border-n200">
          <button
            onClick={() => setRightPanelTab("optionChain")}
            className={`px-4 py-2 text-sm font-medium transition-colors ${rightPanelTab === "optionChain"
              ? "text-p300 bg-p400 border-b-2 border-p150"
              : "text-n500 hover:text-p300"
              }`}>
            Option Chain
          </button>
          <button
            onClick={() => setRightPanelTab("watchlist")}
            className={`px-4 py-2 text-sm font-medium transition-colors ${rightPanelTab === "watchlist"
              ? "text-p300 bg-p400 border-b-2 border-p150"
              : "text-n500 hover:text-p300"
              }`}>
            Watchlist
          </button>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {rightPanelTab === "optionChain" ? (
            <>
              {/* Header - Scalp Mode */}
              <div className="p-4 border-b border-n200">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 rounded px-2 py-1">
                      <img src={ScalpIcon} alt="Scalp" className="w-4 h-4" />
                      <div style={{ lineHeight: "1" }}>
                        <div className="text-[12px] font-semibold text-p300">
                          Scalp
                        </div>
                        <div className="text-[12px] font-semibold text-p300">
                          Mode
                        </div>
                      </div>
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={chartsScalpMode}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setShowScalpConfirmation(true);
                            } else {
                              setChartsScalpMode(false);
                            }
                          }}
                        />
                        <div className="w-9 h-5 bg-n300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-4 after:content-[''] after:absolute after:ml-1 after:mt-1 after:bg-white after:h-3 after:w-3 after:rounded-full after:transition-all relative peer-checked:bg-p150"></div>
                      </label>
                    </div>
                    {chartsScalpMode && (
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] text-n500 font-semibold">
                          Lots
                        </span>
                        <div className="flex gap-1">
                          <input
                            type="text"
                            inputMode="numeric"
                            className="w-16 text-center text-[12px] border border-n300 rounded px-2 py-1 bg-p400 text-p300"
                            value={chartsScalpLotsInput}
                            onChange={(e) => {
                              const onlyDigits = e.target.value.replace(
                                /[^0-9]/g,
                                ""
                              );
                              if (onlyDigits === "") {
                                setChartsScalpLotsInput("");
                                return;
                              }
                              const numValue = parseInt(onlyDigits, 10);
                              if (numValue <= 1000) {
                                setChartsScalpLotsInput(onlyDigits);
                              }
                            }}
                            onBlur={() => {
                              const parsed = parseInt(
                                chartsScalpLotsInput || "1",
                                10
                              );
                              const clampedValue = Math.max(
                                1,
                                Math.min(parsed, 1000)
                              );
                              setChartsScalpLotsInput(String(clampedValue));
                            }}
                            placeholder="1"
                            max="1000"
                            min="1"
                          />
                          <span className="text-[10px] text-n500 mt-1 whitespace-nowrap">
                            Upto: 1000
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="mr-[8px]">
                      <ChartsExpiryDropdown
                        expiry={chartsSelectedExpiry}
                        expiries={chartsSelectedSymbol?.option_expiries || []}
                        onSelect={handleExpirySelect}
                        label="Exp:"
                      />
                    </div>
                    <div className="ml-auto">
                      <ChartsConfigure
                        configureSettingsMap={chartsConfigureSettingsMap}
                        setConfigureSettingsMap={setChartsConfigureSettingsMap}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Option Chain Content */}
              <div className="flex-1 overflow-hidden charts-option-chain bg-p400 flex flex-col">
                {(chartsCurrentOptionsData[chartsSelectedExpiry] && chartsCurrentOptionsData[chartsSelectedExpiry].options && chartsCurrentOptionsData[chartsSelectedExpiry].options.length > 0) ? (
                  <div className="flex-1 min-h-0">
                    <OptionChainTable
                      handleLotxChange={chartsHandleLotxChange}
                      optionData={chartsCurrentOptionsData[chartsSelectedExpiry]}
                      options={chartsCurrentOptionsData[chartsSelectedExpiry]?.options}
                      showCalls={chartsConfigureSettingsMap.showCalls}
                      showPuts={chartsConfigureSettingsMap.showPuts}
                      calls={calls}
                      puts={puts}
                      parentCallback2={handleCallback2}
                      clickEnable={true}
                      symbol={chartsSelectedSymbol}
                      showDelta={chartsConfigureSettingsMap.showDelta}
                      showGamma={chartsConfigureSettingsMap.showGamma}
                      showVega={chartsConfigureSettingsMap.showVega}
                      showRho={chartsConfigureSettingsMap.showRho}
                      showTheta={chartsConfigureSettingsMap.showTheta}
                      showOIChng={chartsConfigureSettingsMap.showOIChng}
                      showOIBuilddup={chartsConfigureSettingsMap.showOIBuilddup}
                      isScalpOrderChecked={chartsScalpMode}
                      scalpLots={parseInt(chartsScalpLotsInput || "1", 10)}
                      maxPain={maxPain}
                      selectedDate={activeDate}
                      setObjectData={setObjectData}
                      objectData={objectData}
                      selectedData={chartsSelectedData}
                      isFromCharts={true}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-n500 p-4">
                    <div className="text-center">
                      <div className="text-lg font-semibold mb-2">
                        {!chartsSelectedSymbol ? "Select a symbol" :
                          !chartsSelectedExpiry ? "Select an expiry" :
                            !chartsCurrentOptionsData[chartsSelectedExpiry] ? "Loading Option Data..." :
                              "No options data available"}
                      </div>
                      {chartsSelectedSymbol && (
                        <div className="text-sm text-n400 mt-2">
                          Symbol: {chartsSelectedSymbol.scrip} ({chartsSelectedSymbol.code})
                        </div>
                      )}
                      {chartsSelectedExpiry && (
                        <div className="text-sm text-n400">
                          Expiry: {chartsSelectedExpiry}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Watchlist Tab Content */
            <div className="flex-1 overflow-hidden flex flex-col p-3">
              <WatchlistContainer symbols={chartsAllSymbols || []} isInChartsTab={true} />
            </div>
          )}
        </div>
      </div>

      {/* Scalp Mode Confirmation Modal */}
      <ConfirmationModal
        isOpen={showScalpConfirmation}
        onConfirm={handleScalpModeConfirm}
        onCancel={handleScalpModeCancel}
        title="Enable Scalp Mode"
        message="Enabling scalp mode will trigger market orders without re-confirmation. Do you wish to continue?"
        confirmText="Yes, Enable"
      />
    </div>
  );
}

export default Charts;
